import { useCallback, useEffect, useState } from 'react';
import { adminApi, AdminAbzug, AdminAbzugCreate, AdminAbzugUpdate } from '../../../../services/api/admin';

interface UseAdminAbzuegeReturn {
  abzuege: AdminAbzug[];
  loading: boolean;
  error: string | null;
  create: (data: AdminAbzugCreate) => Promise<AdminAbzug>;
  update: (id: string, data: AdminAbzugUpdate) => Promise<AdminAbzug>;
  remove: (id: string) => Promise<void>;
}

export const useAdminAbzuege = (orgId: string): UseAdminAbzuegeReturn => {
  const [abzuege, setAbzuege] = useState<AdminAbzug[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await adminApi.getAbzuege(orgId);
      setAbzuege(data);
    } catch {
      setError('Abzugsdaten konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    load();
  }, [load]);

  const create = useCallback(
    async (data: AdminAbzugCreate): Promise<AdminAbzug> => {
      const created = await adminApi.createAbzug(orgId, data);
      setAbzuege((prev) => [...prev, created]);
      return created;
    },
    [orgId],
  );

  const update = useCallback(
    async (id: string, data: AdminAbzugUpdate): Promise<AdminAbzug> => {
      const updated = await adminApi.updateAbzug(orgId, id, data);
      setAbzuege((prev) => prev.map((a) => (a.id === id ? updated : a)));
      return updated;
    },
    [orgId],
  );

  const remove = useCallback(
    async (id: string) => {
      await adminApi.deleteAbzug(orgId, id);
      setAbzuege((prev) => prev.filter((a) => a.id !== id));
    },
    [orgId],
  );

  return { abzuege, loading, error, create, update, remove };
};
